/**
 * AP-35 Detailblatt 6 — **Technik-Blatt, Leistungserklärung, Materialzertifikate.**
 *
 * Der Vertrag zwischen Backend und ERP. Das Schema ist dasselbe wie bei den
 * Zusatzfeldern der Zeiterfassung (`name`, `label`, `type`, `required`,
 * `options`), erweitert um fünf Angaben (2.2). Die Anwendung reagiert nur auf
 * `systemKey`, nie auf den Feldnamen — ein Mandant darf „Werkszeugnis
 * anfordern" umbenennen, die Wirkung bleibt (10.2-2).
 */

/**
 * `user` — ein Mitarbeiter (Wert `{ id, name }`, Name als Schnappschuss für
 * Erklärung und Auftragsmappe). `source` — woher eine Unterlage kommt (Statik,
 * Planung; Wert {@link TechSourceValue}). Beide Nachtrag 4 (19.9.2026).
 */
export type TechFieldType = 'string' | 'number' | 'boolean' | 'date' | 'enum' | 'user' | 'source'

export const TECH_FIELD_TYPES: readonly TechFieldType[] = ['string', 'number', 'boolean', 'date', 'enum', 'user', 'source']

/** Wert eines Feldes vom Typ `user`. */
export interface TechUserValue {
  id: string
  name: string
}

/**
 * Nachtrag 4 — woher Statik oder Planung kommen. `NONE` ist eine Aussage
 * („keine"), kein leeres Feld: Die Position erbt dann nicht mehr vom Projekt.
 */
export const TECH_SOURCE_MODES = ['NONE', 'PROVIDED', 'DERIVED', 'EXTERNAL'] as const
export type TechSourceMode = (typeof TECH_SOURCE_MODES)[number]

/** `{projekt}` steht für den Begriff des Mandanten (Projekt/Auftrag). */
export const TECH_SOURCE_MODE_LABELS: Readonly<Record<TechSourceMode, string>> = {
  NONE: 'keine',
  PROVIDED: 'Bereitgestellt von:',
  DERIVED: 'Abgeleitet von {projekt}:',
  EXTERNAL: 'Extern von:',
}

/** Wert eines Feldes vom Typ `source`. Namen als Schnappschuss, Kennungen zum Wiederfinden. */
export interface TechSourceValue {
  mode: TechSourceMode
  /** PROVIDED/EXTERNAL: der Geschäftspartner */
  clientId?: string | null
  clientName?: string | null
  /** PROVIDED/EXTERNAL: „Verweis auf" — der Ansprechpartner */
  contactId?: string | null
  contactName?: string | null
  /** DERIVED: das Projekt, aus dem die Unterlage stammt */
  projectId?: string | null
  projectLabel?: string | null
}

/** Ein Wert des Technik-Blatts als Text — für Erklärung, Auftragsmappe und Platzhalter. */
export function techWertText(v: unknown, projekt = 'Projekt'): string {
  if (v === null || v === undefined) return ''
  if (v === true) return 'ja'
  if (v === false) return 'nein'
  if (typeof v !== 'object') return String(v)
  const o = v as Partial<TechSourceValue> & Partial<TechUserValue>
  if (typeof o.mode === 'string') {
    if (o.mode === 'NONE') return TECH_SOURCE_MODE_LABELS.NONE
    const art = (TECH_SOURCE_MODE_LABELS[o.mode as TechSourceMode] ?? '').replace('{projekt}', projekt)
    const wer = o.mode === 'DERIVED' ? o.projectLabel : o.clientName
    return [art, wer, o.contactName ? `(${o.contactName})` : null].filter(Boolean).join(' ')
  }
  if (typeof o.name === 'string') return o.name
  return ''
}

/** Blatt 6 2.3 — worauf die Anwendung reagiert, mit dem Typ, den das Feld haben muss. */
export const TECH_SYSTEM_KEYS = {
  requestMillCertificates: 'boolean',
  galvanizingSuitabilityRequired: 'boolean',
  executionClass: 'enum',
  standard: 'enum',
  surface: 'enum',
  welded: 'boolean',
} as const satisfies Record<string, TechFieldType>

export type TechSystemKey = keyof typeof TECH_SYSTEM_KEYS

export const TECH_SYSTEM_KEY_LABELS: Readonly<Record<TechSystemKey, string>> = {
  requestMillCertificates: 'Werkszeugnis anfordern',
  galvanizingSuitabilityRequired: 'Eignung für Feuerverzinkung gefordert',
  executionClass: 'Ausführungsklasse',
  standard: 'Bauteil-Norm',
  surface: 'Oberfläche',
  welded: 'Schweißen',
}

/**
 * Blatt 6 3.2 — Felder der Leistungserklärung, denen ein Technikfeld zugeordnet
 * werden kann (`dopField`). Die mit `leistung: true` bilden die Tabelle
 * „erklärte Leistungen"; die übrigen stehen im Kopf der Erklärung.
 */
export const DOP_FIELDS = {
  componentCategory: { label: 'Bauteil-Kategorie', leistung: false },
  intendedUse: { label: 'Verwendungszweck', leistung: false },
  drawingNumber: { label: 'Zeichnungsnummer', leistung: false },
  standard: { label: 'Norm', leistung: false },
  executionClass: { label: 'Ausführungsklasse', leistung: false },
  tolerances: { label: 'Geometrische Toleranzen', leistung: true },
  weldability: { label: 'Schweißeignung', leistung: true },
  fractureToughness: { label: 'Bruchzähigkeit / Kerbschlagarbeit', leistung: true },
  loadBearing: { label: 'Tragfähigkeit', leistung: true },
  deformationSls: { label: 'Verformung im Grenzzustand der Gebrauchstauglichkeit', leistung: true },
  fatigue: { label: 'Ermüdungsfestigkeit', leistung: true },
  reactionToFire: { label: 'Brandverhalten', leistung: true },
  fireResistance: { label: 'Feuerwiderstand', leistung: true },
  cadmium: { label: 'Freisetzung von Cadmium und seinen Verbindungen', leistung: true },
  radioactivity: { label: 'Freisetzung radioaktiver Strahlung', leistung: true },
  durability: { label: 'Dauerhaftigkeit', leistung: true },
  material: { label: 'Werkstoff', leistung: true },
  manufacturing: { label: 'Herstellung', leistung: true },
  harmonizedSpec: { label: 'Harmonisierte technische Spezifikation', leistung: false },
} as const

export type DopField = keyof typeof DOP_FIELDS

export interface TechField {
  /** fester Schlüssel der Werte (`techFields[name]`), nach dem Anlegen unveränderlich */
  name: string
  label: string
  type: TechFieldType
  required?: boolean
  /**
   * `enum`: die erlaubten Werte. `string`: **Vorschläge** — frei tippen oder
   * aus der Liste wählen (Nachtrag 4). `user`: die Projektrolle, nach der die
   * Mitarbeiter vorgefiltert werden (erster Eintrag).
   */
  options?: string[]
  /** nur `string`: mehrzeilig */
  multiline?: boolean
  systemKey?: TechSystemKey | null
  section?: string | null
  dopField?: DopField | null
  help?: string | null
  showInPwa?: boolean
  /** ausgeblendet statt entfernt, solange Werte daran hängen (2.5) */
  hidden?: boolean
}

export interface TechSchema {
  fields: TechField[]
}

export type TechValues = Record<string, unknown>

/** Ein Feld, aufgelöst Projekt → Position (Blatt 6 2.1, Mechanik Blatt 2 5.). */
export interface ResolvedTechField {
  own: unknown
  effective: unknown
  inherited: boolean
}

/** Blatt 6 2.4 — Herstellerdaten am Mandanten (`ProjectSetting.manufacturer`). */
export interface TechManufacturer {
  name?: string
  address?: string
  plant?: string
  fpcCertificateNumber?: string
  fpcCertificateDate?: string
  notifiedBodyName?: string
  notifiedBodyNumber?: string
  weldingCoordinatorName?: string
  weldingCoordinatorQualification?: string
  weldingCoordinatorCertificate?: string
  signatoryName?: string
  signatoryFunction?: string
  signatoryPlace?: string
  language?: 'DE' | 'EN'
}

/** Pflicht für jede Erklärung (Blatt 6 3.4 Zeile 2). */
export const MANUFACTURER_REQUIRED: readonly (keyof TechManufacturer)[] = [
  'name',
  'address',
  'fpcCertificateNumber',
  'notifiedBodyName',
  'notifiedBodyNumber',
]

export const MANUFACTURER_LABELS: Readonly<Record<keyof TechManufacturer, string>> = {
  name: 'Hersteller',
  address: 'Anschrift',
  plant: 'Werk',
  fpcCertificateNumber: 'WPK-Zertifikat Nr.',
  fpcCertificateDate: 'WPK-Zertifikat ausgestellt am',
  notifiedBodyName: 'Notifizierte Stelle',
  notifiedBodyNumber: 'Kennnummer der notifizierten Stelle',
  weldingCoordinatorName: 'Schweißaufsichtsperson',
  weldingCoordinatorQualification: 'Qualifikation der Schweißaufsicht',
  weldingCoordinatorCertificate: 'Zertifikat der Schweißaufsicht',
  signatoryName: 'Unterzeichner',
  signatoryFunction: 'Funktion des Unterzeichners',
  signatoryPlace: 'Ort der Unterzeichnung',
  language: 'Sprache der Erklärung',
}

/** Blatt 6 3.4 — eine Zeile der Prüfliste. */
export type DeclarationCheckLevel = 'block' | 'warn' | 'info'

export interface DeclarationCheckItem {
  key:
    | 'requiredFields'
    | 'manufacturer'
    | 'executionClass'
    | 'certificates'
    | 'positionReleased'
    | 'positionCompleted'
    | 'drawingNumber'
    | 'previousVersion'
  level: DeclarationCheckLevel
  ok: boolean
  text: string
  /** fehlende Felder, Zeilen o. Ä. */
  details?: string[]
}

export const DECLARATION_STATUSES = ['ISSUED', 'WITHDRAWN'] as const
export type ProjectDeclarationStatus = (typeof DECLARATION_STATUSES)[number]

/** Blatt 5 7.1 — mit der Rangfolge der EN 10204 (3.2 erfüllt 3.1). */
export const MATERIAL_CERTIFICATE_TYPES = [
  'EN10204_2_1',
  'EN10204_2_2',
  'EN10204_3_1',
  'EN10204_3_2',
  'DOC',
  'TEST_REPORT',
  'OTHER',
] as const
export type MaterialCertificateType = (typeof MATERIAL_CERTIFICATE_TYPES)[number]

export const MATERIAL_CERTIFICATE_TYPE_LABELS: Readonly<Record<MaterialCertificateType, string>> = {
  EN10204_2_1: 'EN 10204 2.1',
  EN10204_2_2: 'EN 10204 2.2',
  EN10204_3_1: 'EN 10204 3.1',
  EN10204_3_2: 'EN 10204 3.2',
  DOC: 'Konformitätserklärung',
  TEST_REPORT: 'Prüfbericht',
  OTHER: 'Sonstiges',
}

const RANG: Partial<Record<MaterialCertificateType, number>> = {
  EN10204_2_1: 1,
  EN10204_2_2: 2,
  EN10204_3_1: 3,
  EN10204_3_2: 4,
}

/**
 * Blatt 5 7.2 — erfüllt `vorhanden` die geforderte Art? Innerhalb der EN 10204
 * erfüllt das höherwertige Zeugnis das schwächere; die übrigen Arten nur sich selbst.
 */
export function zeugnisErfuellt(gefordert: MaterialCertificateType, vorhanden: MaterialCertificateType): boolean {
  const g = RANG[gefordert]
  const v = RANG[vorhanden]
  if (g !== undefined && v !== undefined) return v >= g
  return gefordert === vorhanden
}
